import { useMutation } from "@apollo/client";
import Button from "components/base/Button";
import { Input } from "components/base/Form";
import { Alert } from "components/base/Toast";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import PlanTypeSelector from "./components/PlanTypeSelector";
import CustomersSelector from "./CustomersSelector";
import { CREATE_PRODUCTION_PLAN } from "./graphql";

const CreatePlan = ({
  onCreatedPlan,
  initialValues = {},
  disablePlanType = false,
  skipValidateCustomer = false,
}) => {
  const [name, setName] = useState(initialValues.name || "");
  const [planType, setPlanType] = useState(
    initialValues.planType || "AB_LTD_SALES_ORDER",
  );
  const [customer, setCustomer] = useState(initialValues.customer || null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus();
      }, 200);
    }
  }, []);

  const [createPlan, createPlanRes] = useMutation(CREATE_PRODUCTION_PLAN, {
    variables: {
      name,
      wisCustomerId: customer?.id,
      planType,
      orderType: "FOB_ORDER",
      startDate: new Date(),
      lines: initialValues.lines || [],
    },
    onCompleted(res) {
      Alert("success", "Plan Created.");
      onCreatedPlan(res.createProductionPlan.productionPlan);
    },
    onError(error) {
      Alert("error", error.message);
    },
    refetchQueries: ["FETCH_PREV_PLANS"],
  });

  // only valid if user input a name, select customer and the customer has price list.
  function tryCreatePlan(e) {
    e.preventDefault();
    if (name) {
      createPlan();
    } else {
      Alert("error", "Please input a plan name.");
    }
  }

  let customerValidationErrors = [];
  if (customer && !skipValidateCustomer) {
    if (!customer.priceList) {
      customerValidationErrors.push(
        "This customer does not have a price list. ",
      );
    }
    if (!customer.odooPartnerId) {
      customerValidationErrors.push("This customer is not connected to Odoo. ");
    }
  }

  return (
    <form onSubmit={tryCreatePlan}>
      <div className="-mx-2">
        <table>
          <tbody>
            <tr>
              <td className="align-baseline">
                <label>Type: </label>
              </td>
              <td className="w-full">
                <PlanTypeSelector
                  disabled={disablePlanType}
                  value={planType}
                  onChange={(e) => setPlanType(e.target.value)}
                />
              </td>
            </tr>
            {planType === "AB_LTD_SALES_ORDER" && (
              <tr>
                <td className="align-baseline">
                  <label>Customer: </label>
                </td>
                <td className="w-full align-baseline">
                  <CustomersSelector value={customer} onChange={setCustomer} />
                  {customer && customerValidationErrors.length > 0 && (
                    <div className="mx-1 mt-2 rounded-md border border-red-200 bg-red-50/60 p-2 text-sm">
                      <div className="text-red-600">
                        {customerValidationErrors.map((err, index) => (
                          <div key={index}>{err}</div>
                        ))}
                      </div>

                      <div className="mt-1">
                        Please complete the customer setup before creating a
                        plan.
                        <Link
                          className="ml-1 text-blue-600 hover:underline"
                          to={`/sales/customers/${customer.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Click here to edit customer.
                        </Link>
                      </div>
                    </div>
                  )}
                </td>
              </tr>
            )}

            <tr>
              <td>
                <label>Plan Name: </label>
              </td>
              <td className="w-full">
                <Input
                  autoFocus
                  ref={inputRef}
                  className="w-full"
                  placeholder="Give a name to your plan. This will help you identify the plan later."
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <hr />

      <div>
        <Button primary loading={createPlanRes.loading} onClick={tryCreatePlan}>
          Create
        </Button>
      </div>
    </form>
  );
};

export default CreatePlan;
